/**
 * The chat assistant's endpoints.
 *
 * Threads and actions go through the shared client. Sending a message is the
 * one exception: the reply arrives as a Server-Sent Events stream, so it is a
 * plain fetch with a hand-rolled SSE parser. It attaches the bearer token and
 * refreshes it once on a 401, the same way the client does.
 */

import { api, ApiError } from './client'
import { authApi } from './endpoints'
import { useAuthStore } from '@/store/auth'
import type { ChatMessage, ChatThread, ChatThreadSummary, ChatTool, PendingAction } from '@/types/api'

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1'

export const chatApi = {
  threads: () => api.get<ChatThreadSummary[]>('/chat/threads'),
  createThread: () => api.post<ChatThread>('/chat/threads'),
  thread: (id: string) => api.get<ChatThread>(`/chat/threads/${id}`),
  deleteThread: (id: string) => api.delete<void>(`/chat/threads/${id}`),
  confirmAction: (threadId: string, actionId: string) =>
    api.post<{ action: PendingAction; message: ChatMessage }>(`/chat/threads/${threadId}/actions/${actionId}/confirm`),
  dismissAction: (threadId: string, actionId: string) =>
    api.post<{ action: PendingAction }>(`/chat/threads/${threadId}/actions/${actionId}/dismiss`),
}

export interface StreamHandlers {
  onToken?: (text: string) => void
  onTool?: (tool: ChatTool) => void
  onAction?: (action: PendingAction) => void
  onMessage?: (message: ChatMessage) => void
  onError?: (message: string) => void
  onDone?: () => void
}

/** Thrown when the server answers the message POST with an error status. */
export class ChatStreamError extends Error {
  readonly status: number
  constructor(message: string, status: number) {
    super(message)
    this.name = 'ChatStreamError'
    this.status = status
  }
}

async function refreshOnce(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().tokens?.refreshToken
  if (!refreshToken) return null
  try {
    const tokens = await authApi.refresh(refreshToken)
    useAuthStore.setState({ tokens })
    return tokens.accessToken
  } catch {
    useAuthStore.getState().clear()
    return null
  }
}

async function openStream(threadId: string, content: string, signal: AbortSignal | undefined, isRetry: boolean): Promise<Response> {
  const token = useAuthStore.getState().tokens?.accessToken
  const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' })
  if (token) headers.set('Authorization', `Bearer ${token}`)
  const response = await fetch(`${BASE_URL}/chat/threads/${threadId}/messages`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ content }),
    signal,
  })
  if (response.status === 401 && !isRetry) {
    const refreshed = await refreshOnce()
    if (refreshed) return openStream(threadId, content, signal, true)
  }
  return response
}

/**
 * POST a message and read the reply as it streams. Resolves when the stream
 * ends (after `done`, or when the server closes it); rejects on a network
 * failure, an error status, or an abort (as the fetch's AbortError).
 */
export async function streamMessage(
  threadId: string,
  content: string,
  handlers: StreamHandlers,
  signal?: AbortSignal,
): Promise<void> {
  const response = await openStream(threadId, content, signal, false)
  if (!response.ok) {
    let detail = `The assistant answered with status ${response.status}`
    try {
      const problem = (await response.json()) as { detail?: string; title?: string; message?: string }
      detail = problem.detail || problem.message || problem.title || detail
    } catch {
      // A non-JSON body (a proxy page, say) keeps the status message.
    }
    if (response.status === 401) throw new ApiError(detail, 401, 'unauthorized', null)
    throw new ChatStreamError(detail, response.status)
  }
  if (!response.body) throw new ChatStreamError('The assistant sent an empty reply', response.status)

  const reader = response.body.getReader()
  // Stop: cancel the body so the read loop ends even if the fetch does not reject.
  const onAbort = () => void reader.cancel().catch(() => undefined)
  if (signal?.aborted) onAbort()
  signal?.addEventListener('abort', onAbort)
  const decoder = new TextDecoder()
  let buffer = ''
  let finished = false

  const dispatch = (event: string, data: string) => {
    let payload: unknown = {}
    if (data.trim()) {
      try {
        payload = JSON.parse(data)
      } catch {
        // A malformed frame is skipped rather than killing the whole reply.
        return
      }
    }
    const body = (payload ?? {}) as Record<string, unknown>
    switch (event) {
      case 'token':
        if (typeof body.text === 'string') handlers.onToken?.(body.text)
        break
      case 'tool':
        if (body.id && body.name) handlers.onTool?.(body as unknown as ChatTool)
        break
      case 'action':
        if (body.action) handlers.onAction?.(body.action as PendingAction)
        break
      case 'message':
        if (body.message) handlers.onMessage?.(body.message as ChatMessage)
        break
      case 'error':
        handlers.onError?.(typeof body.message === 'string' ? body.message : 'The assistant hit a problem')
        break
      case 'done':
        finished = true
        handlers.onDone?.()
        break
      default:
        break
    }
  }

  const consume = (frame: string) => {
    let event = 'message'
    const data: string[] = []
    for (const rawLine of frame.split('\n')) {
      const line = rawLine.replace(/\r$/, '')
      if (!line || line.startsWith(':')) continue
      const colon = line.indexOf(':')
      const field = colon === -1 ? line : line.slice(0, colon)
      let value = colon === -1 ? '' : line.slice(colon + 1)
      if (value.startsWith(' ')) value = value.slice(1)
      if (field === 'event') event = value
      else if (field === 'data') data.push(value)
    }
    if (data.length === 0 && event === 'message') return
    dispatch(event, data.join('\n'))
  }

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      // Frames end with a blank line; a partial frame stays in the buffer.
      let boundary = buffer.search(/\r?\n\r?\n/)
      while (boundary !== -1) {
        const match = /\r?\n\r?\n/.exec(buffer.slice(boundary))
        const frame = buffer.slice(0, boundary)
        buffer = buffer.slice(boundary + (match?.[0].length ?? 2))
        consume(frame)
        if (finished) return
        boundary = buffer.search(/\r?\n\r?\n/)
      }
    }
    buffer += decoder.decode()
    if (buffer.trim()) consume(buffer)
  } finally {
    signal?.removeEventListener('abort', onAbort)
    reader.releaseLock?.()
  }
}
